import { useEffect } from 'react';
import { useAuth, useSession, useUser } from '@clerk/clerk-react';
import type { UserResource, SessionResource } from '@clerk/types';

/**
 * AuthManager class to handle authentication token management
 * This allows using auth in non-React code (e.g., API utilities)
 */
class AuthManager {
  private _sessionToken: string | null = null;
  private _isTokenRefreshing: boolean = false;

  /**
   * Set the session token
   * @param token The Clerk session token or null if not authenticated
   */
  setSessionToken(token: string | null): void {
    this._sessionToken = token;
  }

  /**
   * Get the current session token
   * @returns The current session token or null if not authenticated
   */
  getSessionToken(): string | null {
    return this._sessionToken;
  }

  /**
   * Get the Authorization header object for API requests
   * @returns Object with Authorization header if authenticated, empty object otherwise
   */
  getAuthHeader(): Record<string, string> {
    return this._sessionToken ? { 'Authorization': `Bearer ${this._sessionToken}` } : {};
  }

  /**
   * Check if the user is authenticated (has a valid token)
   * @returns Boolean indicating if authenticated
   */
  isAuthenticated(): boolean {
    return !!this._sessionToken;
  }

  /**
   * Set token refreshing status
   * @param status Boolean indicating if token is being refreshed
   */
  setTokenRefreshing(status: boolean): void {
    this._isTokenRefreshing = status;
  }

  /**
   * Check if token is currently being refreshed
   * @returns Boolean indicating if token is being refreshed
   */
  isTokenRefreshing(): boolean {
    return this._isTokenRefreshing;
  }
}

// Create and export a singleton instance of AuthManager
export const authManager = new AuthManager();

/**
 * Interface for useApiAuth hook return value
 */
interface UseApiAuthResult {
  isAuthenticated: boolean;
  isLoaded: boolean;
  user: UserResource | null;
}

/**
 * Custom hook to integrate Clerk authentication with API requests
 * 
 * This hook:
 * 1. Updates the auth token when the user's session changes
 * 2. Sets up automatic token refreshing
 * 3. Manages authentication state
 * 
 * Use this hook in a high-level component (like App.tsx)
 */
export const useApiAuth = (): UseApiAuthResult => {
  const { isSignedIn, isLoaded } = useAuth();
  const { user } = useUser();
  const { session } = useSession();
  
  // Update token when authentication state changes
  useEffect(() => {
    const updateAuthToken = async (session?: SessionResource | null) => {
      if (isSignedIn && session) {
        try {
          authManager.setTokenRefreshing(true);
          
          // Get the session token from Clerk
          const token = await session.getToken();
          
          // Update the auth manager with the token
          authManager.setSessionToken(token);
          
          if (process.env.NODE_ENV === 'development') {
            console.log('Authentication token updated');
          }
        } catch (error) {
          console.error('Failed to get session token:', error);
          authManager.setSessionToken(null);
        } finally {
          authManager.setTokenRefreshing(false);
        }
      } else {
        // Clear the token if user is not signed in
        authManager.setSessionToken(null);
        
        if (process.env.NODE_ENV === 'development') {
          console.log('Authentication token cleared');
        }
      }
    };
    
    // Only update when auth is loaded
    if (isLoaded) {
      // Handle session being possibly undefined
      updateAuthToken(session || null);
    }
  }, [isSignedIn, isLoaded, session]);
  
  // Set up a token refresh interval
  useEffect(() => {
    // Only set up refresh interval if user is signed in
    // Early return if user is not signed in or session is undefined/null
    if (!isSignedIn || !session) return;
    
    // Refresh token every 30 minutes to ensure it doesn't expire
    const refreshInterval = setInterval(() => {
      if (!authManager.isTokenRefreshing()) {
        session.getToken().then(token => {
          authManager.setSessionToken(token);
          if (process.env.NODE_ENV === 'development') {
            console.log('Authentication token refreshed');
          }
        }).catch(error => {
          console.error('Error refreshing token:', error);
        });
      }
    }, 30 * 60 * 1000); // 30 minutes
    
    // Cleanup interval on unmount
    return () => {
      clearInterval(refreshInterval);
    };
  }, [isSignedIn, session]);
  
  return {
    isAuthenticated: Boolean(isSignedIn) && authManager.isAuthenticated(),
    isLoaded,
    user: user || null
  };
};

export default useApiAuth;

